import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Marquee from 'components/marquee';
import Spinner from 'components/widget/prices/prices-table/tables/history/affiliation/Spinner';
import { useLocale } from 'locale';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { State } from 'store/state';
import { IInvestorMarketLatestStats, IInvestorsHistory } from 'store/sagas/apiService/types';
import { investorsHistorySelector, investorsLatestStatsSelector, isLoadingInvestorsNationalSalesHistorySelector, isLoadingInvestorsStatsSelector } from 'store/state/domainData/selectors/investors';
import { RealEstateCard } from 'screens/Investors/components/RealEstateCard';
import { DealsHistoryChart, toChartSeries } from 'screens/Investors/components/DealsHistoryChart';
import ArrowCircleDownFilled from 'assets/svg/arrow-circle-down-filled.svg';
import ArrowCircleUpFilled from 'assets/svg/arrow-circle-up-filled.svg';
import { TickerItem, ITickerItem } from './TickerItem';
import { Bar, CardsRow, CtaContent, DataRow, Divider, InfoPart, MarqueeSlot, Panel, PanelInner, SpinnerSlot, StickyRoot, Toggle, ToggleIcon, ToggleText, VisualPart } from './styled';

// Marquee's seamless loop relies on translateX(-50%): the rendered content must be exactly 2
// identical, uninterrupted halves so the wrap point is indistinguishable from the start. Each half
// repeats the 3-item set a few times (rather than rendering it once) so the strip reads as a
// continuous ticker tape instead of 2 visibly-distinct blocks; every item always gets a divider
// (none suppressed) so every seam — including the one between repeated groups — looks identical.
const TICKER_SET_REPEAT_COUNT = 3;

export const MarketTrendTickerBase: React.FC<{
  data: IInvestorMarketLatestStats;
  isLoading: boolean;
  historyData: IInvestorsHistory[];
  historyDataLoading: boolean;
  sticky?: boolean;
  contentWidth?: number;
}> = ({ data, isLoading, historyData, historyDataLoading, sticky, contentWidth }) => {
  const { t } = useLocale();
  const isDevice = useScreenBreakpoint() < 3;
  const [ isExpanded, setIsExpanded ] = useState(false);

  const {
    totalDeals,
    totalDealsMonthDiff,
    investorPurchases,
    investorPurchasesMonthDiff,
    investorSales,
    investorSalesMonthDiff,
    month,
    year,
  } = data;

  const hasData = Number.isFinite(totalDeals);
  const monthName = t('months.fromFirst', { month });

  const listData: ITickerItem[] = [
    { count: totalDeals, delta: totalDealsMonthDiff, title: t('investors.insights.dateTitle', { month: monthName, year: year % 100 }) },
    { count: investorPurchases, delta: investorPurchasesMonthDiff, title: t('investors.marketTicker.investorPurchases') },
    { count: investorSales, delta: investorSalesMonthDiff, title: t('investors.marketTicker.investorSales') },
  ];
  const { years, oldSales, newSales } = toChartSeries(historyData);

  // desktop: only the toggle control (not the whole scrolling bar) opens the panel on hover —
  // matches the Figma toggle component, whose Default/Variant2 states are the collapsed/expanded
  // affordance. The panel stays open while the pointer is over the toggle OR the panel itself; a
  // short close timer bridges the tiny gap when the pointer travels from the toggle down into the
  // panel (they aren't the same DOM subtree), and any re-enter cancels the pending close.
  // mobile: no hover concept — keep the explicit tap-to-toggle behavior on the toggle instead.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const openPanel = () => {
    if (isDevice) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setIsExpanded(true);
  };
  const closePanelDeferred = () => {
    if (isDevice) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsExpanded(false), 120);
  };
  const handleToggleClick = () => {
    if (isDevice) setIsExpanded(prev => !prev);
  };

  const renderTickerSet = (copyKey: string) => (
    Array.from({ length: TICKER_SET_REPEAT_COUNT }).map((_, repIndex) => (
      listData.map((el, i) => (
        <TickerItem key={`${copyKey}-${repIndex}-${i}`} item={el} />
      ))
    ))
  );

  return (
    <StickyRoot sticky={sticky}>
      <Bar data-auto="investors_home_market_ticker">
        <MarqueeSlot>
          {isLoading && !hasData ? (
            <SpinnerSlot><Spinner /></SpinnerSlot>
          ) : hasData ? (
            <Marquee time={90} ltr>
              {renderTickerSet('a')}
              {renderTickerSet('b')}
            </Marquee>
          ) : null}
        </MarqueeSlot>
        {hasData ? (
          <Toggle
            type="button"
            onClick={handleToggleClick}
            onMouseEnter={openPanel}
            onMouseLeave={closePanelDeferred}
            data-auto="investors_home_market_ticker_toggle"
          >
            <CtaContent>
              <ToggleText>{isDevice ? t('investors.marketTicker.toggle.mobile') : t('investors.marketTicker.toggle')}</ToggleText>
              <ToggleIcon aria-hidden>{isExpanded ? <ArrowCircleUpFilled /> : <ArrowCircleDownFilled />}</ToggleIcon>
            </CtaContent>
            <Divider />
          </Toggle>
        ) : null}
      </Bar>
      {isExpanded && hasData ? (
        <Panel
          data-auto="investors_home_market_ticker_panel"
          onMouseEnter={openPanel}
          onMouseLeave={closePanelDeferred}
        >
          <PanelInner contentWidth={contentWidth}>
            <DataRow>
              <InfoPart>
                <RealEstateCard {...listData[0]} variant="ticker" />
                {isDevice ? (
                  <CardsRow>
                    <RealEstateCard {...listData[1]} isSmall variant="ticker" />
                    <RealEstateCard {...listData[2]} isSmall variant="ticker" />
                  </CardsRow>
                ) : (
                  <>
                    <RealEstateCard {...listData[1]} variant="ticker" />
                    <RealEstateCard {...listData[2]} variant="ticker" />
                  </>
                )}
              </InfoPart>
              <VisualPart>
                {historyDataLoading ? null : <DealsHistoryChart years={years} oldSales={oldSales} newSales={newSales} variant="ticker" />}
              </VisualPart>
            </DataRow>
          </PanelInner>
        </Panel>
      ) : null}
    </StickyRoot>
  );
};

const mapStateToProps = (state: State) => ({
  data: investorsLatestStatsSelector(state),
  isLoading: isLoadingInvestorsStatsSelector(state),
  historyData: investorsHistorySelector(state),
  historyDataLoading: isLoadingInvestorsNationalSalesHistorySelector(state),
});

export const MarketTrendTicker = connect(mapStateToProps)(MarketTrendTickerBase);
